import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import Loader from 'components/Loader/Loader'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GAUSIUM_ENABLED, PROGRESS_TYPES, ROBOT_NETWORK_STATUSES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { IRobotInfo, IStation } from 'utils/interfaces'
import { VENDOR } from 'utils/vendorDisplayName'

import FacilityView from './FacilityView/FacilityView'

const Facility = () => {
  const { id } = useParams<{ id: string }>()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [facility, setFacility] = useState<any>(null)
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [stations, setStations] = useState<IStation[]>([])
  const [activeTab, setActiveTab] = useState(0)

  const isFetchingRef = useRef(false)

  const fetchData = async (isInitial = false) => {
    if (isInitial && isFetchingRef.current) return
    isFetchingRef.current = true

    try {
      if (isInitial) {
        setLoadingProgress(PROGRESS_TYPES.WORK)

        // First fetch the facility to get its robots_ids
        const facilityData = await MainApi.fetchFacility(Number(id))
        setFacility(facilityData)

        const facilityRobotIds: number[] = facilityData?.robots_ids || []

        // Now fetch only the facility's robots and stations in parallel
        const [facilityRobots, stationsData] = await Promise.all([
          facilityRobotIds.length > 0
            ? RobotApi.fetchRobots(undefined, undefined, undefined, facilityRobotIds)
            : Promise.resolve({ results: [], count: 0 }),
          StationApi.fetchStations(),
        ])

        setStations(stationsData)
        const filteredRobots = GAUSIUM_ENABLED
          ? facilityRobots.results
          : facilityRobots.results.filter(r => r.vendor !== VENDOR.GAUSIUM)
        setRobots(filteredRobots)

        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } else {
        // During polling, access the latest state safely to get the robot IDs.
        // If an initial fetch is already in progress, this will be bypassed by the guard above
        // to prevent parallel execution during the first few seconds of loading.
        setFacility((prevFacility: any) => {
          if (prevFacility) {
            const facilityRobotIds: number[] = prevFacility.robots_ids || []
            if (facilityRobotIds.length > 0) {
              RobotApi.fetchRobots(undefined, undefined, undefined, facilityRobotIds).then(facilityRobots => {
                const filteredRobots = GAUSIUM_ENABLED
                  ? facilityRobots.results
                  : facilityRobots.results.filter(r => r.vendor !== VENDOR.GAUSIUM)
                setRobots(filteredRobots)
              })
            }
          }
          return prevFacility
        })
      }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        if (isInitial) setLoadingProgress(PROGRESS_TYPES.IDLE)
        return
      }
      if (isInitial) setLoadingProgress(PROGRESS_TYPES.ERROR)
      console.warn('[Facility] fetchData error:', error)
    } finally {
      isFetchingRef.current = false
    }
  }

  useEffect(() => {
    fetchData(true)
  }, [id])

  useEffect(() => {
    const interval = setInterval(() => {
      // Fetch robots only on Assets tab (1) as requested by user
      if (activeTab === 1) {
        fetchData()
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab])

  if (loadingProgress === PROGRESS_TYPES.WORK) return <Loader />
  if (loadingProgress === PROGRESS_TYPES.ERROR) return <div>Error loading facility</div>
  if (!facility) return null

  return (
    <FacilityView
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      facility={facility}
      robots={robots.sort((a, b) => {
        if (a.networkStatus !== b.networkStatus) {
          return a.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? -1 : 1
        }
        const diagA = a.systemDiagnostics?.level ?? 0
        const diagB = b.systemDiagnostics?.level ?? 0
        if (diagA !== diagB) return diagB - diagA
        if (a.emergencyButton !== b.emergencyButton) return a.emergencyButton ? -1 : 1
        return a.serialNumber.localeCompare(b.serialNumber)
      })}
      stations={stations}
      onRobotsUpdated={() => fetchData()}
      onStationsChanged={async () => {
        const stationsData = await StationApi.fetchStations()
        setStations(stationsData)
      }}
    />
  )
}

export default Facility
